import { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Phone, X as XIcon } from 'lucide-react'
import Sidebar from './Sidebar'
import Header from './Header'
import ImpersonationBar from './ImpersonationBar'
import useIsMobile from '../hooks/useIsMobile'
import { useAuth } from '../contexts/AuthContext'
import { useNeedsMobile } from '../hooks/useNotifications'

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const { data: needsMobile } = useNeedsMobile(isAuthenticated)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  // Data-dense table pages use the full browser width instead of the
  // max-w-7xl reading cap, so more columns fit as the window widens.
  const fullWidth = location.pathname.startsWith('/lbp-builders')

  // Prevent body scroll while the mobile drawer is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [mobileNavOpen])

  return (
    <div className="flex h-[100dvh] bg-grey-50">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Skip to main content
      </a>

      {/* Desktop sidebar (collapse toggle) — hidden entirely on mobile */}
      {!isMobile && (
        <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      )}

      {/* Mobile navigation drawer + backdrop */}
      {isMobile && (
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
              mobileNavOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <div
            className={`fixed left-0 top-0 h-full z-50 transform transition-transform duration-300 ${
              mobileNavOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <Sidebar isOpen mobile onToggle={() => setMobileNavOpen(false)} />
          </div>
        </>
      )}

      {/* Main Content */}
      {/* min-w-0 overrides the flex item's default min-width:auto, which
          otherwise refuses to shrink below the widest unwrapped descendant
          (e.g. the horizontally-scrollable filter-pill rows) — without it
          this column, and everything in it, floors at ~1150px and the whole
          page scrolls sideways instead of just those rows. */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
        isMobile ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'
      }`}>
        <ImpersonationBar />
        <Header sidebarOpen={sidebarOpen} onMenuClick={() => setMobileNavOpen(true)} />

        {needsMobile && !bannerDismissed && (
          <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">
              Add your mobile number so we can forward lead replies to you —{' '}
              <Link to="/profile" className="font-medium underline hover:no-underline">
                update your profile
              </Link>
            </span>
            <button onClick={() => setBannerDismissed(true)} aria-label="Dismiss" className="p-1 hover:bg-amber-100 rounded flex-shrink-0">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        <main id="main-content" className={`flex-1 overflow-y-auto ${isMobile ? 'p-3' : 'p-6'}`}>
          <div className={fullWidth ? 'w-full' : 'max-w-7xl mx-auto'}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
